import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPlaceholderSupabase, supabaseUrl, supabaseAnonKey } from '../lib/supabase';

export type TtsProvider = 'system' | 'cloud' | 'eleven';

export interface UseTextToSpeechOptions {
  /** 'system' = Web Speech API (free, instant). 'cloud' = OpenAI tts-1-hd
   *  via the deployed `tts` Supabase edge function (~$0.003/reply).
   *  'eleven' = ElevenLabs Turbo v2.5 via the same edge function with
   *  `provider: 'eleven'` (~$0.02/reply, premium quality — used for the
   *  pro-coach review surface and any coach who flips Brain's voice). */
  provider: TtsProvider;
  /** Playback rate, 0.5–2.0. Default 1.0. ElevenLabs ignores. */
  rate?: number;
  /** Voice identifier:
   *  - system: voiceURI from window.speechSynthesis.getVoices()
   *  - cloud:  one of OpenAI's 11 voices
   *    (alloy/ash/ballad/coral/echo/fable/nova/onyx/sage/shimmer/verse)
   *  - eleven: a friendly key ('daniel', 'charlotte', 'george', 'lily')
   *    OR a raw 20-char ElevenLabs voice_id. The edge function normalises. */
  voiceId?: string | null;
}

export interface UseTextToSpeechResult {
  /** Speak `text` immediately. Stops any in-flight utterance first. */
  speak: (text: string) => void;
  /** Speak `text` split on .!? boundaries — better UX for long replies
   *  (cloud TTS hits a 4000-char cap per request anyway). */
  speakSentenceChunked: (text: string) => void;
  /** Cancel all queued audio. */
  stop: () => void;
  pause: () => void;
  resume: () => void;
  /** True while any chunk is playing or queued. */
  isSpeaking: boolean;
  /** Available system voices for the picker. Empty for cloud + Firefox. */
  systemVoices: SpeechSynthesisVoice[];
}

const MAX_CLOUD_CHARS = 3500; // leave headroom under OpenAI's 4096 cap

/** Split on sentence boundaries while preserving the punctuation mark
 *  on the chunk it ends. Empty / whitespace-only chunks dropped. */
function splitSentences(text: string): string[] {
  const out: string[] = [];
  let buf = '';
  for (const ch of text) {
    buf += ch;
    if (/[.!?]/.test(ch) && buf.trim().length > 0) {
      out.push(buf.trim());
      buf = '';
    }
  }
  if (buf.trim().length > 0) out.push(buf.trim());
  return out;
}

/**
 * Voice-out hook for Brain replies. Two providers:
 *
 * - `system` — `window.speechSynthesis`. Free, instant, OS-dependent
 *   quality. Excellent on macOS/Safari, decent on Chrome, mediocre on
 *   Windows, unsupported on Firefox.
 * - `cloud` — OpenAI tts-1-hd via the deployed `supabase/functions/tts`
 *   edge function. Smoother + consistent. Costs ~$0.003 per reply.
 *
 * Mirrors the web1 hook signature so the callers (BrainChatPanel,
 * SessionNew, etc) port across without changes.
 */
export function useTextToSpeech(opts: UseTextToSpeechOptions): UseTextToSpeechResult {
  const { provider, rate = 1.0, voiceId = null } = opts;

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [systemVoices, setSystemVoices] = useState<SpeechSynthesisVoice[]>([]);

  // Cloud queue + active audio element refs — keep playback serial so
  // sentence chunks finish in order.
  const cloudQueueRef = useRef<string[]>([]);
  const cloudActiveRef = useRef<HTMLAudioElement | null>(null);
  // True while a single drain loop is running. Set SYNCHRONOUSLY when a drain
  // starts so back-to-back speakCloud() calls in one tick (speakSentenceChunked
  // pushes every sentence at once) enqueue instead of each starting its own
  // concurrent playback chain — which would overlap into multiple voices.
  const cloudDrainingRef = useRef(false);
  // Bumped by stop(). An in-flight fetch/playback chain compares against its
  // captured value and aborts if it changed — so a stopped/replaced utterance
  // can't resolve later and play over the current one.
  const cloudGenRef = useRef(0);

  // Populate system voices once they load. Some browsers (Chrome) fire
  // `voiceschanged` async after first call to getVoices().
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const update = () => {
      const v = window.speechSynthesis.getVoices();
      // Filter to the user's primary locale prefix (default 'en').
      const lang = (navigator.language ?? 'en').slice(0, 2).toLowerCase();
      setSystemVoices(v.filter((voice) => voice.lang.toLowerCase().startsWith(lang)));
    };
    update();
    window.speechSynthesis.addEventListener('voiceschanged', update);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', update);
  }, []);

  // Cancel everything in flight — system + cloud.
  const stop = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    // Invalidate any in-flight cloud chain + reset the drain gate.
    cloudGenRef.current += 1;
    cloudDrainingRef.current = false;
    cloudQueueRef.current = [];
    if (cloudActiveRef.current) {
      cloudActiveRef.current.pause();
      cloudActiveRef.current.src = '';
      cloudActiveRef.current = null;
    }
    setIsSpeaking(false);
  }, []);

  // ---- system provider ----------------------------------------------------

  const speakSystem = useCallback((text: string) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = rate;
    if (voiceId) {
      const match = window.speechSynthesis.getVoices().find((v) => v.voiceURI === voiceId);
      if (match) utt.voice = match;
    }
    utt.onstart = () => setIsSpeaking(true);
    utt.onend = () => {
      // Only flip to false when the queue is fully drained — pending
      // utterances may still be queued.
      const sp = window.speechSynthesis;
      if (!sp.pending && !sp.speaking) setIsSpeaking(false);
    };
    utt.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [rate, voiceId]);

  // ---- cloud provider -----------------------------------------------------

  const playNextCloud = useCallback(async () => {
    const myGen = cloudGenRef.current;
    const next = cloudQueueRef.current.shift();
    if (!next) {
      cloudDrainingRef.current = false;
      setIsSpeaking(false);
      return;
    }
    if (isPlaceholderSupabase()) {
      // Offline — silently drain.
      void playNextCloud();
      return;
    }
    try {
      // Provider mapping: 'cloud' → OpenAI, 'eleven' → ElevenLabs. Both
      // share the same edge function which switches on body.provider.
      const upstreamProvider = provider === 'eleven' ? 'eleven' : 'openai';
      const defaultVoice = provider === 'eleven' ? 'daniel' : 'alloy';
      // Raw fetch, NOT supabase.functions.invoke. invoke() only blob-decodes
      // `application/octet-stream`/`application/pdf` responses; the tts
      // function returns `audio/mpeg`, which invoke falls back to decoding as
      // text — corrupting the MP3 into an unplayable string (silent voice).
      // Reading the ArrayBuffer ourselves keeps the bytes intact.
      const res = await fetch(`${supabaseUrl}/functions/v1/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          text: next,
          provider: upstreamProvider,
          voice: voiceId ?? defaultVoice,
          speed: rate,
          format: 'mp3',
        }),
      });
      // A stop()/replacement happened while fetching — abort rather than
      // play over whatever is current now.
      if (cloudGenRef.current !== myGen) return;
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn('[tts] fetch failed', res.status);
        void playNextCloud();
        return;
      }
      const buf = await res.arrayBuffer();
      if (cloudGenRef.current !== myGen) return;
      const blob = new Blob([buf], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      cloudActiveRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        cloudActiveRef.current = null;
        if (cloudGenRef.current === myGen) void playNextCloud();
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        cloudActiveRef.current = null;
        if (cloudGenRef.current === myGen) void playNextCloud();
      };
      await audio.play();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[tts] cloud playback threw', err);
      if (cloudGenRef.current === myGen) void playNextCloud();
    }
  }, [voiceId, rate, provider]);

  const speakCloud = useCallback((text: string) => {
    // Push every <=3500-char chunk; if the input was already short,
    // we just queue the whole thing.
    const trimmed = text.trim();
    if (!trimmed) return;
    const chunks: string[] = [];
    let cursor = 0;
    while (cursor < trimmed.length) {
      chunks.push(trimmed.slice(cursor, cursor + MAX_CLOUD_CHARS));
      cursor += MAX_CLOUD_CHARS;
    }
    cloudQueueRef.current.push(...chunks);
    setIsSpeaking(true);
    // Start a SINGLE drain loop. cloudDrainingRef is flipped synchronously so
    // the rest of a one-tick burst (speakSentenceChunked pushing every
    // sentence) just enqueues instead of each kicking a concurrent chain.
    if (!cloudDrainingRef.current) {
      cloudDrainingRef.current = true;
      void playNextCloud();
    }
  }, [playNextCloud]);

  // ---- public API ---------------------------------------------------------

  // Both 'cloud' (OpenAI) and 'eleven' (ElevenLabs) flow through the
  // playNextCloud queue — they only differ in the upstream provider
  // body passed to the edge function. The non-system branch handles both.
  const isCloudish = provider === 'cloud' || provider === 'eleven';

  const speak = useCallback((text: string) => {
    stop();
    if (isCloudish) speakCloud(text);
    else speakSystem(text);
  }, [isCloudish, speakCloud, speakSystem, stop]);

  const speakSentenceChunked = useCallback((text: string) => {
    stop();
    const sentences = splitSentences(text);
    if (sentences.length === 0) return;
    if (isCloudish) {
      // Push each sentence separately so playback can stop mid-reply
      // without buffering the whole thing.
      for (const s of sentences) speakCloud(s);
    } else {
      // System speech queues utterances natively — push them in one
      // pass and let the browser play them serially.
      for (const s of sentences) speakSystem(s);
    }
  }, [isCloudish, speakCloud, speakSystem, stop]);

  const pause = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.pause();
    }
    cloudActiveRef.current?.pause();
  }, []);

  const resume = useCallback(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.resume();
    }
    cloudActiveRef.current?.play().catch(() => {});
  }, []);

  // Stop on unmount so the page change doesn't leave audio bleeding.
  useEffect(() => () => stop(), [stop]);

  return useMemo(
    () => ({ speak, speakSentenceChunked, stop, pause, resume, isSpeaking, systemVoices }),
    [speak, speakSentenceChunked, stop, pause, resume, isSpeaking, systemVoices],
  );
}
